import { create } from 'zustand'

// Define simplified interfaces based on what we need
interface Organization {
  id: string
  name: string
  slug?: string | null
  imageUrl?: string | null
  membersCount?: number
  [key: string]: unknown // Allow for other properties we might need
}

interface OrganizationMembership {
  id: string
  role: string
  organization: Organization
  [key: string]: unknown // Allow for other properties we might need
}

interface OrganizationState {
  activeOrganization: Organization | null
  organizationMemberships: OrganizationMembership[] | null
  organizations: Organization[] // List of all organizations user has access to
  isLoaded: boolean
  isChangingOrg: boolean

  // Actions
  setActiveOrganization: (organization: Organization | null) => void
  setOrganizationMemberships: (
    memberships: OrganizationMembership[] | null
  ) => void
  setOrganizations: (organizations: Organization[]) => void
  addOrganization: (organization: Organization) => void
  updateOrganization: (organization: Organization) => void
  removeOrganization: (organizationId: string) => void
  setIsLoaded: (isLoaded: boolean) => void
  setIsChangingOrg: (isChanging: boolean) => void
  reset: () => void
}

export const useOrganizationStore = create<OrganizationState>((set) => ({
  // State
  activeOrganization: null,
  organizationMemberships: null,
  organizations: [],
  isLoaded: false,
  isChangingOrg: false,

  // Actions
  setActiveOrganization: (organization) =>
    set((state) => ({ ...state, activeOrganization: organization })),

  setOrganizationMemberships: (memberships) =>
    set((state) => ({ ...state, organizationMemberships: memberships })),

  setOrganizations: (organizations) =>
    set((state) => ({ ...state, organizations })),

  addOrganization: (organization) =>
    set((state) => ({
      ...state,
      organizations: [...state.organizations, organization],
    })),

  updateOrganization: (organization) =>
    set((state) => ({
      ...state,
      organizations: state.organizations.map((org) =>
        org.id === organization.id ? organization : org
      ),
    })),

  removeOrganization: (organizationId) =>
    set((state) => ({
      ...state,
      organizations: state.organizations.filter(
        (org) => org.id !== organizationId
      ),
    })),

  setIsLoaded: (isLoaded) => set((state) => ({ ...state, isLoaded })),

  setIsChangingOrg: (isChangingOrg) =>
    set((state) => ({ ...state, isChangingOrg })),

  reset: () =>
    set(() => ({
      activeOrganization: null,
      organizationMemberships: null,
      organizations: [],
      isLoaded: false,
      isChangingOrg: false,
    })),
}))

// Helper selectors for common state access patterns
export const useActiveOrganization = () =>
  useOrganizationStore((state) => state.activeOrganization)

export const useOrganizationMemberships = () =>
  useOrganizationStore((state) => state.organizationMemberships)

export const useOrganizations = () =>
  useOrganizationStore((state) => state.organizations)

export const useOrganizationLoadingState = () => ({
  isLoaded: useOrganizationStore((state) => state.isLoaded),
  isChangingOrg: useOrganizationStore((state) => state.isChangingOrg),
})
